// a_star.ts
import type GraphNode from "./graph_node";

type XY = { x: number; y: number; z?: number };

interface PQItem<T> {
	node: GraphNode<T>;
	f: number;       // g + h
	g: number;       // cumulative cost (including penalties)
	turns: number;   // integer turn count (for tie-break/inspection)
	hops: number;    // edges so far (final tie-breaker)
}

const EPS = 1e-12;

// --- your original "what counts as a turn" (kept for the integer counter) ---
const TURN_DEG = 15;
const TURN_RAD = (TURN_DEG * Math.PI) / 180;

// --- NEW knobs: tune to your graph scale ---
const ALPHA_TURN = 0.10;        // per-radian turn penalty (e.g., 0.05..0.30)
const BETA_ALIGN = 0.02;        // per-radian misalignment to the goal direction
const GAMMA_DETOUR = 0.00;      // penalty per meter when moving farther from goal (0..0.05)
const FORBID_UTURN = true;      // drop ~180° immediate reversals
const MAX_TURN_RAD = Math.PI/2; // drop steps that exceed this turn (e.g., > 90°). Set to Math.PI to disable.

/** Pull {x,y,z} from node.coords (defaults to 0) */
function getXY<T>(n: GraphNode<T>): XY {
	const c = (n as any).coords ?? {};
	return { x: c.x ?? 0, y: c.y ?? 0, z: c.z ?? 0 };
}

/** Euclidean distance (heuristic = euclidLB(1)) */
function euclid<T>(a: GraphNode<T>, b: GraphNode<T>): number {
	const A = getXY(a), B = getXY(b);
	return Math.hypot(A.x - B.x, A.y - B.y, (A.z ?? 0) - (B.z ?? 0));
}

function vec<T>(from: GraphNode<T>, to: GraphNode<T>) {
	const A = getXY(from), B = getXY(to);
	return { x: B.x - A.x, y: B.y - A.y, z: (B.z ?? 0) - (A.z ?? 0) };
}

function angleBetweenVec(a: {x:number;y:number;z:number}, b: {x:number;y:number;z:number}) {
	const la = Math.hypot(a.x, a.y, a.z), lb = Math.hypot(b.x, b.y, b.z);
	if (la < EPS || lb < EPS) return 0;
	const cos = (a.x*b.x + a.y*b.y + a.z*b.z) / (la*lb);
	return Math.acos(Math.max(-1, Math.min(1, cos)));
}

/** Does going p->u->v constitute a "turn" (for integer counter)? */
function isTurn<T>(p: GraphNode<T>, u: GraphNode<T>, v: GraphNode<T>): boolean {
	const a = vec(p, u), b = vec(u, v);
	const angle = angleBetweenVec(a, b);
	return angle > TURN_RAD;
}

/** Array-backed priority queue: ordered by f, then fewer turns, then fewer hops, then smaller g */
function pushOrdered<T>(q: Array<PQItem<T>>, item: PQItem<T>) {
	const idx = q.findIndex((x) =>
		x.f > item.f + EPS ||
		(Math.abs(x.f - item.f) <= EPS && (
			x.turns > item.turns ||
			(x.turns === item.turns && (
				x.hops > item.hops ||
				(x.hops === item.hops && x.g > item.g + EPS)
			))
		))
	);
	if (idx === -1) q.push(item);
	else q.splice(idx, 0, item);
}

/**
 * A* that minimizes travel cost + smoothness penalties.
 * Travel edge cost = distance * (weight ?? 1), clamped non-negative.
 * Penalties added to g on each step:
 *  - ALPHA_TURN * angle(prev->cur, cur->next)
 *  - BETA_ALIGN * angle(cur->next, cur->goalDirection)
 *  - GAMMA_DETOUR * max(0, dist(next,goal) - dist(cur,goal))
 * Additionally, we can forbid sharp turns via FORBID_UTURN/MAX_TURN_RAD.
 */
function aStar<T>(start: GraphNode<T>, goal: GraphNode<T>): Array<GraphNode<T>> {
	if (start === goal) return [start];
	if (!start.links?.length) return [];

	// Best labels by node id
	const gBest = new Map<symbol, number>();          // best cumulative cost (with penalties)
	const tBest = new Map<symbol, number>();          // best turn count achieving gBest
	const parent = new Map<symbol, GraphNode<T> | undefined>(); // for path & turn calc

	// For computing turn angle at a node, we keep the best-known incoming direction at that node
	const inDir = new Map<symbol, {x:number;y:number;z:number} | undefined>();

	const open: Array<PQItem<T>> = [];
	const f0 = euclid(start, goal); // admissible heuristic scale (minWeight=1)

	gBest.set(start.id, 0);
	tBest.set(start.id, 0);
	parent.set(start.id, undefined);
	inDir.set(start.id, undefined); // no incoming direction at start
	pushOrdered(open, { node: start, f: f0, g: 0, turns: 0, hops: 0 });

	while (open.length) {
		const cur = open.shift() as PQItem<T>;

		// Lazy-stale check: skip if this isn't the current best label
		const gNow = gBest.get(cur.node.id);
		const tNow = tBest.get(cur.node.id);
		if (
			gNow == null ||
			cur.g > gNow + EPS ||
			(Math.abs(cur.g - gNow) <= EPS && (tNow ?? Infinity) < cur.turns)
		) {
			continue;
		}

		if (cur.node === goal) break;

		const dirToGoal = vec(cur.node, goal);
		const incoming = inDir.get(cur.node.id); // may be undefined at start

		for (const n of cur.node.links) {
			const d = cur.node.distances?.[n.id];
			if (d == null) continue;

			const w = Math.max(cur.node.weights?.[n.id] ?? 1, 0); // clamp negative to 0
			const travel = d * w;                                 // >= 0
			if (!(travel >= 0)) continue;

			const stepDir = vec(cur.node, n);

			// Continuous turn angle (radians) for penalization
			let turnAngle = 0;
			if (incoming) turnAngle = angleBetweenVec(incoming, stepDir);

			// Hard filters for sharp turns (kill zig/zag)
			if (FORBID_UTURN && turnAngle > Math.PI - 1e-3) continue;
			if (turnAngle > MAX_TURN_RAD + 1e-9) continue;

			// Alignment (prefer smaller angle to goal direction) and "moving away" penalty
			const alignAngle = angleBetweenVec(stepDir, dirToGoal);
			const away = Math.max(0, euclid(n, goal) - euclid(cur.node, goal));

			// Penalties added to the travel cost
			const penalty =
				ALPHA_TURN * turnAngle +
				BETA_ALIGN * alignAngle +
				GAMMA_DETOUR * away;

			const c = travel + penalty;

			// integer turn counter (for tiebreak): use your original threshold
			const p = parent.get(cur.node.id);
			const addTurn = p ? (isTurn(p, cur.node, n) ? 1 : 0) : 0;

			const g2 = cur.g + c;
			const t2 = cur.turns + addTurn;
			const h2 = euclid(n, goal);       // admissible (scaled by minWeight=1)

			const gBestN = gBest.get(n.id);
			const tBestN = tBest.get(n.id);

			const improves =
				gBestN == null ||
				g2 < gBestN - EPS ||
				(Math.abs(g2 - gBestN) <= EPS && t2 < (tBestN ?? Infinity));

			if (improves) {
				gBest.set(n.id, g2);
				tBest.set(n.id, t2);
				parent.set(n.id, cur.node);
				inDir.set(n.id, stepDir); // store incoming direction at n
				pushOrdered(open, { node: n, f: g2 + h2, g: g2, turns: t2, hops: cur.hops + 1 });
			}
		}
	}

	if (!parent.has(goal.id)) return [];
	const path: Array<GraphNode<T>> = [goal];
	while (path[0] !== start) {
		const p = parent.get(path[0].id);
		if (!p) return [];
		path.unshift(p);
	}
	return path;
}

export default aStar;
